import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	useAddAiChatMessageNonAdminMutation,
	AddAiChatMessageNonAdminMutationVariables
} from "../../graphql/mutations/addAiChatMessageNonAdmin.generated";
import { useMessagesByPkQuery } from "../../graphql/queries/getMessage.generated";
import { useUpdateAiChatMutation } from "../../graphql/mutations/updateAiChatMutation.generated";
import { useGetUserQuery } from "../../graphql/queries/getUser.generated";
import {
	Box,
	Container,
	TextField,
	IconButton,
	Paper,
	CircularProgress,
	Avatar,
	Typography,
	Divider,
	Button,
	Popover
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useAuth } from "../../context/AuthContext";
import styles from "./ai-chat-conversation-nonadmin.style";
import { ApolloError } from "@apollo/client";
import routeNames from "../../constants/route-names";
import Markdown from "react-markdown";

interface NormalizedMessage {
	id: string;
	body: string;
	senderId: string | null;
	user: {
		firstName?: string;
		lastName?: string;
		profilePhotoLink?: string;
	} | null;
	createdAt?: string;
}

const AiChatConversationNonAdmin = () => {
	const { loggedUser } = useAuth();
	const navigate = useNavigate();
	const { messageId: rawMessageId, chatId: rawChatId } = useParams<{
		messageId?: string;
		chatId?: string;
	}>();

	const messageId = rawMessageId !== "new" ? rawMessageId : undefined;
	const initialChatId = rawChatId !== "new" ? rawChatId : null;

	const [chatId, setChatId] = useState<string | null>(initialChatId ?? null);
	const [userMessage, setUserMessage] = useState<string>("");
	const [chatMessages, setChatMessages] = useState<any[]>([]);
	const [userMessageError, setUserMessageError] = useState<string>("");
	const [showFeedBackElements, setShowFeedbackElements] = useState<boolean | null>(true);
	const [isHelpful, setIsHelpful] = useState<boolean | null>(null);
	const [feedbackText, setFeedbackText] = useState<string>("");
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const preprocessMarkdown = useCallback((messageBody: string): string => {
		if (!messageBody) return "";

		if (messageBody[0] === "`") {
			return messageBody
				.replace(/```markdown\s*/g, ``)
				.replace(/```$/g, ``)
				.trim();
		} else {
			return messageBody;
		}
	}, []);

	const transformQueryMessage = useCallback((message: any): NormalizedMessage => {
		return {
			id: message.id,
			body: message.body,
			senderId: message.senderId,
			user: message.user || null,
			createdAt: message.createdAt
		};
	}, []);

	const transformMutationMessage = useCallback(
		(message: any): NormalizedMessage => ({
			id: message.message.id,
			body: message.message.body,
			senderId: message.senderId,
			user: message.message.user || null,
			createdAt: message.message.createdAt
		}),
		[]
	);

	const { data: existingMessageData, loading: loadingMessage } = useMessagesByPkQuery({
		variables: { id: messageId as string },
		skip: !messageId
	});

	useEffect(() => {
		if (existingMessageData?.messages_by_pk) {
			const main = existingMessageData.messages_by_pk;
			const threads = main.messageThreads || [];
			const fullThread = [main, ...threads].map(transformQueryMessage);
			setChatMessages(fullThread);
		}
	}, [existingMessageData]);

	const handleOnCompleted = useCallback(
		(data: any) => {
			if (data?.newChatMessage && data?.newChatMessage.length) {
				const newMessages = data.newChatMessage.map(transformMutationMessage);
				setChatId(data.newChatMessage[0].chatId);
				setChatMessages(newMessages);
			}

			if (chatId !== data.newChatMessage[0].chatId) {
				navigate(
					`${routeNames.AI_CHATS_NONADMIN}/${data.newChatMessage[0].chatId}/${data.newChatMessage[0].message.id}`
				);
			}
		},
		[navigate, chatId]
	);

	const [sendMessage, { loading }] = useAddAiChatMessageNonAdminMutation({
		onCompleted: handleOnCompleted,
		onError: (err: any) => console.error("Mutation error:", err)
	});

	const handleOnErrorsGetUsers = useCallback((error: ApolloError) => {
		console.log("Get Users Error: ", error);
	}, []);

	const {
		data,
		loading: userLoading,
		refetch
	} = useGetUserQuery({
		skip: !loggedUser?.uid,
		variables: {
			id: loggedUser?.uid
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "cache-first",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorsGetUsers
	});

	const handleSend = () => {
		if (!userMessage.trim()) {
			setUserMessageError("Message cannot be empty");
			return;
		}

		if (!data?.users_by_pk?.id || !data?.users_by_pk?.workspaces[0]?.workspaceId) {
			setUserMessageError("Missing user data");
			return;
		}

		const variables: AddAiChatMessageNonAdminMutationVariables = {
			message: userMessage,
			userId: data?.users_by_pk?.id,
			workspaceId: data?.users_by_pk?.workspaces[0]?.workspaceId,
			workspaceLocationId: 10,
			chatSource: "web"
		};

		if (chatId) {
			variables.chatId = chatId;
		}

		if (messageId) {
			variables.parentMessageId = messageId;
		}

		setChatMessages(prev => [
			...prev,
			{
				id: "temp-id",
				body: userMessage,
				senderId: data?.users_by_pk?.id || "temp-id",
				user: data?.users_by_pk
					? {
							firstName: data.users_by_pk.firstName,
							lastName: data.users_by_pk.lastName,
							profilePhotoLink: data.users_by_pk.profilePhotoLink
						}
					: null
			}
		]);
		if (!chatId) {
			setChatId("12345");
		}
		setUserMessage("");
		sendMessage({ variables });
	};

	const handleOnErrorAiChatMutation = useCallback((error: ApolloError) => {
		console.log(
			`useUpdateAiChatMutation Mutation fails on ai-chat-conversation-nonadmin file. ${error}`
		);
	}, []);

	const handleOnCompletedAiChatMutation = useCallback(() => {
		console.log(data, "data");
	}, []);

	const [updateAiChat, { loading: mutationLoading }] = useUpdateAiChatMutation({
		errorPolicy: "none",
		onError: handleOnErrorAiChatMutation,
		onCompleted: handleOnCompletedAiChatMutation
	});

	const handleOnPressSubmit = useCallback(() => {
		if (!chatId) return;

		updateAiChat({
			variables: {
				set: {
					helpful: isHelpful,
					feedback: feedbackText
				},
				id: chatId
			}
		});

		setAnchorEl(null);
		setFeedbackText("");
		setShowFeedbackElements(false);
	}, [chatId, isHelpful, feedbackText]);

	const chatMessagesElements = useMemo(() => {
		return (
			<Box sx={styles.conversationContainer}>
				{!chatId ? (
					<Paper elevation={0} sx={styles.onboardingPrompt}>
						<Avatar
							src={process.env.PUBLIC_URL + "/assets/images/hignome-avatar.svg"}
							alt="HiGnome"
							sx={styles.hignomeAvatarOnboarding}
						/>
						<Box>
							<Typography variant="subtitle1" fontWeight="600">
								{`Hi ${data?.users_by_pk?.firstName}!`}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{`I’ve been trained on your organization’s programs and educational resources.`}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{`What can I help you with today?`}
							</Typography>
						</Box>
					</Paper>
				) : (
					chatMessages.map(msg => {
						const isSystem = !msg.senderId;
						const { id, body, user } = msg;

						if (isSystem) {
							return (
								<Box key={`system-chat-message-${id}`} sx={styles.systemMessagecontainer}>
									<Avatar
										alt="HiGnome"
										src={process.env.PUBLIC_URL + "/assets/images/hignome-avatar.svg"}
										sx={styles.hignomeAvatar}
									/>
									<Paper square={false} elevation={3} sx={styles.systemMessage}>
										<Markdown>{preprocessMarkdown(body.trimStart())}</Markdown>
									</Paper>
								</Box>
							);
						} else {
							const { firstName, lastName, profilePhotoLink } = user || {};
							return (
								<Box key={`chat-message-${id}`} sx={styles.userMessageContainer}>
									<Paper square={false} elevation={3} sx={styles.userMessage}>
										<Markdown>{preprocessMarkdown(body.trimStart())}</Markdown>
									</Paper>
									<Avatar alt={`${firstName} ${lastName}`} src={profilePhotoLink || undefined} />
								</Box>
							);
						}
					})
				)}
			</Box>
		);
	}, [chatMessages, chatId, data?.users_by_pk]);

	const feedbackElements = useMemo(() => {
		const thumbsUpSrc =
			isHelpful === true
				? `${process.env.PUBLIC_URL}/assets/images/thumbs-up-filled.svg`
				: `${process.env.PUBLIC_URL}/assets/images/thumbs-up-unfilled.svg`;

		const thumbsDownSrc =
			isHelpful === false
				? `${process.env.PUBLIC_URL}/assets/images/thumbs-down-filled.svg`
				: `${process.env.PUBLIC_URL}/assets/images/thumbs-down-unfilled.svg`;

		const handleUpvote = (event: any) => {
			const newIsHelpful = isHelpful === true ? null : true;
			setIsHelpful(newIsHelpful);

			if (newIsHelpful !== null) {
				setAnchorEl(event.currentTarget.parentElement);
			} else {
				setAnchorEl(null);
			}
		};

		const handleDownvote = (event: any) => {
			const newIsHelpful = isHelpful === false ? null : false;
			setIsHelpful(newIsHelpful);

			if (newIsHelpful !== null) {
				setAnchorEl(event.currentTarget.parentElement);
			} else {
				setAnchorEl(null);
			}
		};

		const open = anchorEl !== null;

		return (
			<Box>
				{!chatId || !showFeedBackElements || loading ? null : (
					<Box sx={styles.feedbackBaseContainer}>
						<Box sx={styles.feedbackContent}>
							<Typography sx={styles.feedbackText}>Was this HiGnome chat helpful?</Typography>
							<Box sx={styles.voteIconsContainer}>
								<Box sx={{ ...styles.voteOptionBox, ...styles.thumbsUpBox }}>
									<img
										src={thumbsUpSrc}
										alt="thumbs up"
										onClick={handleUpvote}
										style={styles.thumbIcon}
									/>
									<Typography sx={styles.voteLabel}>YES</Typography>
								</Box>
								<Box sx={styles.voteOptionBox}>
									<img
										src={thumbsDownSrc}
										alt="thumbs down"
										onClick={handleDownvote}
										style={styles.thumbIcon}
									/>
									<Typography sx={styles.voteLabel}>NO</Typography>
								</Box>
							</Box>
						</Box>
						<Popover
							open={open}
							anchorEl={anchorEl}
							onClose={() => setAnchorEl(null)}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "left"
							}}
							transformOrigin={{
								vertical: "top",
								horizontal: "left"
							}}
							disableScrollLock={false}
						>
							<Box sx={styles.popoverBox}>
								<Box sx={styles.popoverHeader}>
									<Typography variant="body2" sx={styles.popoverHeaderText}>
										Share feedback below to help HiGnome improve
									</Typography>
									<img
										src={`${process.env.PUBLIC_URL}/assets/images/x-black.svg`}
										onClick={() => {
											setAnchorEl(null);
										}}
									/>
								</Box>
								<TextField
									fullWidth
									multiline
									rows={3}
									variant="outlined"
									placeholder="What else would you like to share about your chat with HiGnome? How can we make it better?"
									value={feedbackText}
									onChange={e => setFeedbackText(e.target.value)}
									sx={styles.feedbackTextField}
								/>
								<Button
									variant="contained"
									onClick={() => handleOnPressSubmit()}
									disabled={!feedbackText.trim()}
									sx={styles.submitButton}
								>
									Submit
								</Button>
							</Box>
						</Popover>
					</Box>
				)}
			</Box>
		);
	}, [chatId, isHelpful, setIsHelpful, feedbackText, anchorEl, showFeedBackElements, loading]);

	return (
		<Container maxWidth={false} sx={styles.container}>
			<div style={{ width: "100%" }}>
				<h1 className="screen-title" style={{ marginBottom: "24px" }}>
					HiGnome AI Assistant
				</h1>
			</div>

			{chatMessagesElements}

			{loading || userLoading || loadingMessage ? (
				<Box sx={styles.loadingContainer}>
					<CircularProgress />
				</Box>
			) : null}

			<Paper component="form" sx={styles.inputContainer}>
				<TextField
					variant="filled"
					id="outlined-multiline-static"
					label={chatId ? "Ask a follow up..." : "Ask your question here..."}
					fullWidth
					error={!!userMessageError}
					helperText={userMessageError}
					value={userMessage}
					onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
						setUserMessage(event.target.value);
						if (event.target.value && userMessageError) {
							setUserMessageError("");
						}
					}}
					onBlur={() => {
						if (userMessage.length) {
							setUserMessageError("");
						} else {
							setUserMessageError("Message is required.");
						}
					}}
					onKeyDown={event => {
						if (event.key === "Enter" && !event.shiftKey) {
							event.preventDefault();
							handleSend();
						}
					}}
					InputProps={{
						disableUnderline: true,
						sx: styles.inputProps
					}}
					InputLabelProps={{
						sx: styles.fieldLabel
					}}
				/>
				<Divider sx={styles.inputDivider} orientation="vertical" />
				<IconButton color="default" size="large" sx={{ p: "10px" }} onClick={handleSend}>
					<SendIcon />
				</IconButton>
			</Paper>
			{feedbackElements}
		</Container>
	);
};

export default AiChatConversationNonAdmin;
